import { Vector } from '@ardoq/graph';
import { BLOCKS_VIEW_HANDLE_GRABSIZE } from '../consts';
import { rectContains } from '../misc/geometry';
import { BlocksViewNode, Controller, DragOperation } from '../types';
import { isGroup } from '../viewModel$/util';
import { hitTestHandles } from '../visual/handles';
import { hitTestLink } from '../visual/link';
import { HitTestResult, hitTestNodePosition } from '../visual/node/node';

export enum NodeHit {
  None = 0,
  Expand = 1,
  Url = 2,
  Body = 4,
  Left = 8,
  Right = 16,
  Top = 32,
  Bottom = 64,
  LeftTop = Left | Top,
  RightTop = Right | Top,
  RightBottom = Right | Bottom,
  LeftBottom = Left | Bottom,
  Resize = Left | Top | Right | Bottom,
}

const getNodeHit = (node: BlocksViewNode, pos: Vector, resolution: number) => {
  let ret = NodeHit.None;
  const hitHandles = hitTestHandles(
    node.bounds,
    pos,
    BLOCKS_VIEW_HANDLE_GRABSIZE * resolution
  );

  ret = ret | (hitHandles & DragOperation.Left ? NodeHit.Left : 0);
  ret = ret | (hitHandles & DragOperation.Top ? NodeHit.Top : 0);
  ret = ret | (hitHandles & DragOperation.Right ? NodeHit.Right : 0);
  ret = ret | (hitHandles & DragOperation.Bottom ? NodeHit.Bottom : 0);

  const hitPosition = hitTestNodePosition(node, pos);

  if (isGroup(node)) {
    ret = ret | (hitPosition === HitTestResult.EXPANDER ? NodeHit.Expand : 0);
  }

  ret = ret | (hitPosition === HitTestResult.LINK_ICON ? NodeHit.Url : 0);

  if (ret === 0 && hitPosition === HitTestResult.HIT) {
    ret = NodeHit.Body;
  }

  return ret;
};

export const updateHover = (
  controller: Controller,
  selectedNode: BlocksViewNode | null
) => {
  const { graph, hover, projection, pointerWorld } = controller;
  const oldItem = controller.hover.link() ?? controller.hover.node();

  const doit = (node: BlocksViewNode): BlocksViewNode | null => {
    let hit = null;

    if (rectContains(node.bounds, pointerWorld)) {
      if (isGroup(node) && node.open) {
        for (const child of node.children) {
          hit = doit(child);

          if (hit) {
            break;
          }
        }
      }

      hit = node.parent && !hit ? node : hit;
    }

    return hit;
  };

  let nodeHit = NodeHit.None;
  const resolution = projection.resolution(false);

  hover.clear();

  if (graph && graph.root) {
    const slop = 4 * resolution;

    for (const link of graph.edges) {
      if (hitTestLink(link, pointerWorld, slop)) {
        hover.add(link);
      }
    }

    if (selectedNode && !controller.hover.link()) {
      nodeHit = getNodeHit(selectedNode, pointerWorld, resolution);

      if (nodeHit !== NodeHit.None) {
        hover.add(selectedNode);
      }
    }

    if (!controller.hover.node() && !controller.hover.link()) {
      const hitNode = doit(graph.root);

      if (hitNode) {
        hover.add(hitNode);
        nodeHit = getNodeHit(hitNode, pointerWorld, resolution);
      }
    }
  }

  const hoverNode = controller.hover.node();
  const hoverLink = controller.hover.link();
  const newItem = controller.hover.link() ?? controller.hover.node();

  if (newItem !== oldItem) {
    controller.cancelTooltip();
    controller.renderCanvas();
  }

  if (hoverLink) {
    controller.showLinkTooltip(hoverLink, controller.pointerWorld);
  }

  if (hoverNode) {
    controller.showNodeTooltip(hoverNode, controller.pointerWorld);
  }

  return nodeHit;
};
